import {PAYMENT_ACCOUNT_PATH_FIELD_NAME} from "../utils/form-contact-fields";
export {PAYMENT_ACCOUNT_PATH_FIELD_NAME} from "../utils/form-contact-fields";
import PropTypes from "prop-types";
import {useContext, useEffect, useRef, useState} from "react";
import axios from "axios";
import {loadStripe} from "@stripe/stripe-js/pure";
import {CardElement, Elements, useElements, useStripe} from "@stripe/react-stripe-js";
import FormContext from "@autocx/forms/src/form-context";
import Checkbox from "@autocx/forms/src/controls/checkbox";
import CustomRadioGroup from "@autocx/forms/src/controls/radio";
import PaymentPreview from "@autocx/forms/src/controls/payment";
import WebsiteCardElement from "./website-card-element";
import {
    composeSavedCardDisclosure,
    SAVED_CARD_DISCLOSURE
} from "@autocx/forms/src/utils/payment-consent";
import {formatPaymentMethodBrand, formatSavedPaymentMethod} from "./payment-method-utils";

export {composeSavedCardDisclosure, SAVED_CARD_DISCLOSURE};
export {formatPaymentMethodBrand, formatSavedPaymentMethod} from "./payment-method-utils";

const PAYMENT_RECOVERY_PREFIX = "vert:website-payment:v1";

const PAYMENT_ACCOUNT_PATH_GUEST = "guest";
const PAYMENT_ACCOUNT_PATH_REGISTER = "register";
const WEBSITE_PAYMENT_CARD_OPTIONS = {hidePostalCode: true, disableLink: true};
const inMemoryGuestAccessTokens = new Map();
const TERMINAL_FAILURE_STATUSES = new Set(["failed", "released", "expired"]);
const RESUMABLE_ACTION_STATUSES = new Set(["requiresaction"]);
const REPLAYABLE_OPERATION_STATUSES = new Set(["pending", ...RESUMABLE_ACTION_STATUSES]);
const PAYMENT_UNAVAILABLE_MESSAGE = "Secure card entry isn't available right now. Please try again later.";
const PAYMENT_LOADING_MESSAGE = "Secure card entry is still loading. Wait a moment and try again.";
const dollarsToCents = (value) => {
    const number = Number.parseFloat(value);
    if (Number.isNaN(number)) return null;
    return Math.round(number * 100);
};

const normaliseStatus = (status) => (status || "").toString().toLowerCase().replace(/_/g, "");

export const getCustomerPaymentErrorMessage = (error) => {
    const message = error?.message?.trim() || "";
    const code = error?.code?.toString().toLowerCase();
    if (code === "card_declined" || /card was declined|non-test card/i.test(message)) {
        return "This card was declined. Check the details or try another card.";
    }
    return message || "The card could not be authorised. Check the details or try another card.";
};

export const getPaymentCustomerAccountMode = (field = {}) => {
    if (field.mode !== "take-payment") return "required";
    const rawConfiguredMode = field.customerAccountMode;
    const configuredMode = rawConfiguredMode == null
        ? ""
        : rawConfiguredMode.toString().trim().toLowerCase();
    if (["none", "optional", "required"].includes(configuredMode)) return configuredMode;
    // Preserve the original guest-only boolean only while the explicit setting is genuinely absent. An invalid
    // explicit value must fail closed instead of allowing a stale compatibility flag to weaken account ownership.
    return configuredMode === "" && field.allowGuestPayment === true ? "none" : "required";
};

export const sanitisePaymentFormValues = (values = {}) => {
    const {[PAYMENT_ACCOUNT_PATH_FIELD_NAME]: _paymentAccountPath, ...safeValues} = values;
    return safeValues;
};

const createIdempotencyKey = () => (
    typeof window !== "undefined" && window.crypto?.randomUUID
        ? window.crypto.randomUUID()
        : `${Date.now()}-${Math.random()}`
);

const createGuestAccessToken = () => {
    if (typeof window === "undefined" || !window.crypto) return null;
    if (window.crypto.getRandomValues) {
        const bytes = new Uint8Array(32);
        window.crypto.getRandomValues(bytes);
        return Array.from(bytes, byte => byte.toString(16).padStart(2, "0")).join("");
    }
    return window.crypto.randomUUID?.() || null;
};

// Keep personal details out of the storage key. This is only a stable client-side namespace; the server remains
// authoritative for customer ownership and rejects a key replayed by a different authenticated customer.
const fingerprint = (value) => {
    let hash = 2166136261;
    for (const character of value) {
        hash ^= character.charCodeAt(0);
        hash = Math.imul(hash, 16777619);
    }
    return (hash >>> 0).toString(36);
};

const getCustomerRecoveryIdentity = (formValues, customerSession, guestPayment = false) => {
    if (guestPayment) return "guest";
    const customer = formValues?.customer || {};
    const submittedIdentity = customer.id
        || customer.uuid
        || customer.emailAddress?.trim().toLowerCase()
        || customer.primaryPhoneNumber?.replace(/ /g, "")
        || customer.contactNumber?.replace(/ /g, "");

    if (submittedIdentity) return `customer-${fingerprint(String(submittedIdentity))}`;
    if (customerSession?.isAuthenticated) {
        return `signed-in-${fingerprint(customerSession.initials?.trim().toLowerCase() || "customer")}`;
    }
    return "new-customer";
};

const getPaymentRecoveryStorage = () => {
    try {
        return typeof window !== "undefined" ? window.sessionStorage : null;
    } catch (_error) {
        return null;
    }
};

const getPaymentRecoveryScopePrefix = ({channel, formId, fieldId}) => [
    PAYMENT_RECOVERY_PREFIX,
    encodeURIComponent(channel || ""),
    encodeURIComponent(formId || ""),
    encodeURIComponent(fieldId || "")
].join(":") + ":";

const getPaymentRecoveryKey = (scope) => (
    getPaymentRecoveryScopePrefix(scope) + getCustomerRecoveryIdentity(
        scope.formValues,
        scope.customerSession,
        scope.guestPayment
    )
);

const readPaymentRecovery = (storage, storageKey) => {
    if (!storage) return null;
    try {
        const parsed = JSON.parse(storage.getItem(storageKey));
        return parsed?.idempotencyKey ? parsed : null;
    } catch (_error) {
        return null;
    }
};

const writePaymentRecovery = (storage, storageKey, recovery) => {
    if (!storage) return;
    try {
        storage.setItem(storageKey, JSON.stringify(recovery));
    } catch (_error) {
        // Payment still works when storage is unavailable; server-side idempotency remains in force for this render.
    }
};

const removePaymentRecoveryKeys = (storage, matches) => {
    if (!storage) return;
    try {
        const matchingKeys = [];
        for (let index = 0; index < storage.length; index++) {
            const key = storage.key(index);
            if (key && matches(key)) matchingKeys.push(key);
        }
        matchingKeys.forEach(key => storage.removeItem(key));
    } catch (_error) {
        // Session expiry still protects provider operations if browser storage is unavailable.
    }
};

const guestAccessTokenKey = (channel, formId, operationId) => [
    encodeURIComponent(channel || ""),
    encodeURIComponent(formId || ""),
    String(operationId ?? "")
].join(":");

const rememberGuestAccessToken = (channel, formId, operationId, token) => {
    if (operationId == null || !token) return;
    inMemoryGuestAccessTokens.set(guestAccessTokenKey(channel, formId, operationId), token);
};

export const clearWebsitePaymentRecoveryForSession = () => {
    const storage = getPaymentRecoveryStorage();
    removePaymentRecoveryKeys(storage, key => {
        if (!key.startsWith(`${PAYMENT_RECOVERY_PREFIX}:`)) return false;
        // Signing out invalidates customer-owned attempts, but a guest continuation is deliberately independent of
        // account state and may be the only way to acknowledge a provider-confirmed payment after a redirect.
        return !readPaymentRecovery(storage, key)?.guestAccessToken;
    });
};

export const getOrCreateWebsitePaymentRecovery = (scope, requestSignature) => {
    const storage = getPaymentRecoveryStorage();
    const storageKey = getPaymentRecoveryKey(scope);
    let recovery = readPaymentRecovery(storage, storageKey);
    let wasCreated = false;

    // A definitively unsuccessful operation can never become a charge. The next explicit submit is a new attempt.
    if (!recovery || TERMINAL_FAILURE_STATUSES.has(normaliseStatus(recovery.status))) {
        wasCreated = true;
        recovery = {
            idempotencyKey: createIdempotencyKey(),
            guestAccessToken: scope.guestPayment ? createGuestAccessToken() : undefined,
            requestSignature,
            createdAtUtc: new Date().toISOString()
        };
        writePaymentRecovery(storage, storageKey, recovery);
    }

    return {storage, storageKey, recovery, wasCreated};
};

export const getExistingWebsitePaymentRecovery = (scope) => {
    const storage = getPaymentRecoveryStorage();
    const storageKey = getPaymentRecoveryKey(scope);
    const recovery = readPaymentRecovery(storage, storageKey);
    if (recovery && !TERMINAL_FAILURE_STATUSES.has(normaliseStatus(recovery.status))) {
        const browserOwnsGuestRecovery = !!recovery.guestAccessToken;
        const browserOwnsUnstartedDirectRecovery = !recovery.operationId &&
            !!recovery.idempotencyKey && !!recovery.requestSignature;
        return {
            storage,
            storageKey,
            recovery,
            wasCreated: false,
            needsAutomaticReconciliation: !!recovery.operationId,
            // Authenticated recoveries are rechecked server-side before any locally saved details are shown. This
            // also handles a new inline registration changing the browser key from submitted email to signed-in user.
            // A guest token is itself the browser's continuation credential, including when Start's response was lost
            // before the operation id could be stored locally. A direct-key record without an operation has no
            // provider/customer details to disclose; Start remains authoritative after card details are re-entered.
            ownershipVerified: browserOwnsGuestRecovery || browserOwnsUnstartedDirectRecovery ||
                scope.customerSession?.isAuthenticated !== true
        };
    }

    if (!storage || scope.customerSession?.isAuthenticated !== true) return null;
    try {
        const prefix = getPaymentRecoveryScopePrefix(scope);
        const candidates = [];
        for (let index = 0; index < storage.length; index++) {
            const candidateKey = storage.key(index);
            if (!candidateKey?.startsWith(prefix)) continue;
            const candidate = readPaymentRecovery(storage, candidateKey);
            if ((!candidate?.operationId && !candidate?.guestAccessToken) ||
                TERMINAL_FAILURE_STATUSES.has(normaliseStatus(candidate.status))) continue;
            candidates.push({storage, storageKey: candidateKey, recovery: candidate});
        }
        if (candidates.length !== 1) return null;
        const browserOwnsGuestRecovery = !!candidates[0].recovery.guestAccessToken;
        return {
            ...candidates[0],
            wasCreated: false,
            ownershipVerified: browserOwnsGuestRecovery,
            needsAutomaticReconciliation: !!candidates[0].recovery.operationId
        };
    } catch (_error) {
        return null;
    }
};

export const hasWebsitePaymentRecoveryRequestChanged = (recovery, requestSignature) => (
    !!recovery?.requestSignature && recovery.requestSignature !== requestSignature
);

const discardUnstartedWebsitePaymentRecovery = ({storage, storageKey}) => {
    if (!storage) return;
    try {
        storage.removeItem(storageKey);
    } catch (_error) {
        // No provider request was made, so losing this newly-created browser record is safe.
    }
};

export const updateWebsitePaymentRecovery = (context, operation) => {
    const {storage, storageKey, recovery} = context;
    const updated = {
        ...recovery,
        operationId: operation?.id ?? recovery.operationId,
        status: operation?.status ?? recovery.status,
        mode: operation?.mode ?? recovery.mode,
        amount: operation?.amount ?? recovery.amount,
        currency: operation?.currency ?? recovery.currency,
        updatedAtUtc: new Date().toISOString()
    };
    writePaymentRecovery(storage, storageKey, updated);
    return {...context, storage, storageKey, recovery: updated};
};

export const clearWebsitePaymentRecoveryForResponse = (channel, formId, values) => {
    // Website response custom properties are flattened at the API boundary. Accept the legacy nested shape while
    // callers transition, but prefer the same top-level operation reference that the response converter persists.
    const operationId = values?.paymentOperationId ?? values?.customProperties?.paymentOperationId;
    const storage = getPaymentRecoveryStorage();
    if (operationId == null) return;

    inMemoryGuestAccessTokens.delete(guestAccessTokenKey(channel, formId, operationId));
    if (!storage) return;

    const scopePrefix = [
        PAYMENT_RECOVERY_PREFIX,
        encodeURIComponent(channel || ""),
        encodeURIComponent(formId || "")
    ].join(":") + ":";

    removePaymentRecoveryKeys(storage, key => {
        if (!key.startsWith(scopePrefix)) return false;
        const recovery = readPaymentRecovery(storage, key);
        return String(recovery?.operationId) === String(operationId);
    });
};

export const formPaymentResult = (field, operation) => ({
    // WebsiteFormResponseModel treats unknown top-level properties as custom response data. Nesting this under a
    // `customProperties` object causes the converter to discard the operation link during final form submission.
    paymentOperationId: operation.id,
    [field.name || field.id]: {
        status: operation.status,
        mode: operation.mode,
        amount: operation.amount,
        currency: operation.currency
    }
});

export const getWebsitePaymentAccessTokenForResponse = (channel, formId, values) => {
    const operationId = values?.paymentOperationId ?? values?.customProperties?.paymentOperationId;
    if (operationId == null) return null;
    const remembered = inMemoryGuestAccessTokens.get(guestAccessTokenKey(channel, formId, operationId));
    if (remembered) return remembered;

    const storage = getPaymentRecoveryStorage();
    if (!storage) return null;

    const scopePrefix = [
        PAYMENT_RECOVERY_PREFIX,
        encodeURIComponent(channel || ""),
        encodeURIComponent(formId || "")
    ].join(":") + ":";
    try {
        for (let index = 0; index < storage.length; index++) {
            const key = storage.key(index);
            if (!key?.startsWith(scopePrefix)) continue;
            const recovery = readPaymentRecovery(storage, key);
            if (String(recovery?.operationId) === String(operationId) && recovery?.guestAccessToken) {
                return recovery.guestAccessToken;
            }
        }
    } catch (_error) {
        // The in-memory path above remains available when browser storage becomes inaccessible mid-submission.
    }
    return null;
};

export const buildPaymentOperationResponseSnapshot = (formValues, formId, guestPayment = false) => {
    // Continuation credentials are endpoint inputs, never form data. A retry after a processor result must not copy
    // the raw guest secret into the canonical pending response.
    const {
        paymentOperationAccessToken: _paymentOperationAccessToken,
        guestAccessToken: _guestAccessToken,
        ...responseValues
    } = formValues || {};
    const safeValues = sanitisePaymentFormValues(responseValues);
    if (guestPayment && safeValues.customer) {
        // Guest contact details still belong to the form response (for example, a donation receipt), but an account
        // credential never does. The server snapshots this contact without creating or linking a Customer.
        const {password: _password, ...guestContact} = safeValues.customer;
        safeValues.customer = guestContact;
    }
    return {
        ...safeValues,
        // The route identifies the authoritative form. Keep the submitted response data, but do not send the rendered
        // form configuration (including its shorthand channel code) through the response-model deserializer.
        form: {uuid: formId}
    };
};

export const formatPaymentAmount = (amount, currency = "AUD", country) => {
    const numericAmount = Number(amount);
    if (!Number.isFinite(numericAmount)) return currency;

    const locale = country ? `en-${country}` : currency === "NZD" ? "en-NZ" : "en-AU";
    const formattedAmount = new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(numericAmount / 100);
    return `${formattedAmount} ${currency}`.trim();
};

const parseRecoveryRequestSignature = (recovery) => {
    try {
        return JSON.parse(recovery?.requestSignature || "");
    } catch (_error) {
        return null;
    }
};

const sameOptionalValue = (left, right) => String(left ?? "") === String(right ?? "");

export const getPaymentRecoveryPresentation = (recovery, field, consentText, consentVersion, country) => {
    if (!recovery) return null;
    const request = parseRecoveryRequestSignature(recovery);
    if (!request) return {compatible: false};

    const recoveredAmount = recovery.amount ?? request.amount;
    const minimumAmount = Number(field.minimumAmount);
    const maximumAmount = Number(field.maximumAmount);
    const amountWithinCurrentBounds = !field.customerEntersAmount || (
        Number.isFinite(Number(recoveredAmount)) && Number(recoveredAmount) > 0 &&
        (!Number.isFinite(minimumAmount) || Number(recoveredAmount) >= minimumAmount) &&
        (!Number.isFinite(maximumAmount) || Number(recoveredAmount) <= maximumAmount)
    );
    const compatible =
        sameOptionalValue(request.mode, field.mode) &&
        sameOptionalValue(request.currency, field.currency) &&
        sameOptionalValue(request.fixedAmount, field.fixedAmount) &&
        sameOptionalValue(request.holdDurationHours, field.holdDurationHours) &&
        sameOptionalValue(request.consentVersion, consentVersion) &&
        sameOptionalValue(request.consentText, consentText) &&
        amountWithinCurrentBounds;

    if (!compatible) return {compatible: false};

    const status = normaliseStatus(recovery.status) || "pending";
    const formattedAmount = recoveredAmount == null
        ? null
        : formatPaymentAmount(recoveredAmount, recovery.currency || field.currency || "AUD", country);
    const operationDescription = field.mode === "security-hold"
        ? "card hold"
        : field.mode === "take-payment"
            ? "payment"
            : "saved card";
    const message = status === "succeeded"
        ? `Your ${formattedAmount ? `${formattedAmount} ` : ""}${operationDescription} is confirmed. Submit the form to finish.`
        : status === "requiresaction"
            ? `Continue the verification needed for your ${formattedAmount ? `${formattedAmount} ` : ""}${operationDescription}.`
            : `Your previous ${formattedAmount ? `${formattedAmount} ` : ""}${operationDescription} wasn't completed. Check the card details and try again.`;

    return {
        compatible: true,
        amount: recoveredAmount,
        amountInputValue: recoveredAmount == null ? "" : (Number(recoveredAmount) / 100).toFixed(2),
        paymentMethodId: request.paymentMethodId ?? null,
        restoreConsent: !!recovery.operationId && request.consentAccepted === true,
        status,
        message
    };
};

export const getPaymentConfirmationOptions = (card, usesSavedPaymentMethod, resumesProviderAction) => (
    usesSavedPaymentMethod || resumesProviderAction ? undefined : {payment_method: {card}}
);

export const getCustomerAmountHelp = (field, country) => {
    if (!field.customerEntersAmount || !field.minimumAmount || !field.maximumAmount) return null;
    const currency = field.currency || "AUD";
    return `Enter an amount from ${formatPaymentAmount(field.minimumAmount, currency, country)} to ${formatPaymentAmount(field.maximumAmount, currency, country)}.`;
};

export const getPaymentSignInHref = () => {
    if (typeof window === "undefined") return "/my-account/sign-in";
    const returnUrl = `${window.location.pathname || "/"}${window.location.search || ""}${window.location.hash || ""}`;
    return `/my-account/sign-in?returnUrl=${encodeURIComponent(returnUrl)}`;
};

export const getPaymentConfigurationError = (configuration, field = {}) => {
    const accountMode = getPaymentCustomerAccountMode(field);
    const hasBaseConfiguration = configuration?.publishableKey && configuration?.stripeAccountId &&
        configuration?.consentText && configuration?.consentVersion;
    const hasGuestConfiguration = accountMode !== "optional" ||
        (configuration?.guestConsentText && configuration?.guestConsentVersion);
    return hasBaseConfiguration && hasGuestConfiguration ? null : PAYMENT_UNAVAILABLE_MESSAGE;
};

export const getPaymentPurpose = (field, country) => {
    const currency = field.currency || "AUD";
    const fixedAmount = formatPaymentAmount(field.fixedAmount, currency, country);
    const holdCaptureExplanation = field.allowHoldCapture
        ? " The held amount may later be taken as payment under the terms you agree to below."
        : "";

    switch (field.mode) {
        case "security-hold":
            return field.customerEntersAmount
                ? `Choose an amount in ${currency} for a temporary card hold. No payment will be taken now.${holdCaptureExplanation}`
                : `A temporary hold of ${fixedAmount} will be placed on your card. No payment will be taken now.${holdCaptureExplanation}`;
        case "take-payment":
            return field.customerEntersAmount
                ? `Choose an amount to pay in ${currency}, then enter your card details.`
                : null;
        case "card-capture":
        default:
            return null;
    }
};

const getHoldDurationMessage = (field) => {
    if (field.mode !== "security-hold" || !field.holdDurationHours) return null;

    const hours = Number(field.holdDurationHours);
    if (!Number.isFinite(hours) || hours <= 0) return null;
    return `This hold can remain for up to ${hours} ${hours === 1 ? "hour" : "hours"}. Your bank may release it sooner.`;
};

function PaymentFieldFrame({field, country, children}) {
    const label = field.label || "Card details";
    const holdDurationMessage = getHoldDurationMessage(field);
    const purpose = getPaymentPurpose(field, country);

    return (
        <div className="flex flex-col gap-4 border-t border-gray-200 pt-4">
            <div>
                <div className="text-base font-medium leading-6">
                    {label}<span className="sr-only"> required</span>
                </div>
                {purpose ? (
                    <p className="mb-0 mt-1 text-sm leading-5 opacity-75">{purpose}</p>
                ) : null}
                {holdDurationMessage ? (
                    <p className="mb-0 mt-1 text-xs leading-5 opacity-65">{holdDurationMessage}</p>
                ) : null}
            </div>
            {children}
            {field.description ? (
                <p className="mb-0 text-xs leading-5 opacity-65 whitespace-normal">
                    {field.description}
                </p>
            ) : null}
        </div>
    );
}

PaymentFieldFrame.propTypes = {
    field: PropTypes.object.isRequired,
    country: PropTypes.string,
    children: PropTypes.node
};

export function PaymentElementInner({
    field,
    consentText,
    consentVersion,
    guestConsentText,
    guestConsentVersion,
    savedPaymentMethods = []
}) {
    const stripe = useStripe();
    const elements = useElements();
    const formContext = useContext(FormContext);
    const websiteContext = formContext.websiteContext || {};
    const registerProcessor = formContext.registerProcessor;
    const formId = formContext.id;
    const channel = websiteContext.channel;
    const customerIsAuthenticated = websiteContext.customerSession?.isAuthenticated;
    const customerAccountMode = getPaymentCustomerAccountMode(field);
    const allowsInlineRegistration = customerAccountMode !== "none" && field.allowInlineRegistration !== false;
    const [paymentCustomerEstablished, setPaymentCustomerEstablished] = useState(false);
    const hasPaymentCustomer = !!customerIsAuthenticated || paymentCustomerEstablished;
    const [accountPathChoice, setAccountPathChoice] = useState(() => (
        formContext.values?.[PAYMENT_ACCOUNT_PATH_FIELD_NAME] === PAYMENT_ACCOUNT_PATH_REGISTER
            ? PAYMENT_ACCOUNT_PATH_REGISTER
            : PAYMENT_ACCOUNT_PATH_GUEST
    ));
    const [recoveryContextState, setRecoveryContextState] = useState(() => {
        const recoveryScope = {
            channel,
            formId,
            fieldId: field.id,
            formValues: formContext.values,
            customerSession: websiteContext.customerSession
        };
        if (customerAccountMode === "none" || customerAccountMode === "optional") {
            const guestRecovery = getExistingWebsitePaymentRecovery({...recoveryScope, guestPayment: true});
            if (guestRecovery?.recovery?.guestAccessToken) return guestRecovery;
        }
        const initiallyGuest = customerAccountMode === "none" || (
            customerAccountMode === "optional" && !hasPaymentCustomer &&
            accountPathChoice !== PAYMENT_ACCOUNT_PATH_REGISTER
        );
        return getExistingWebsitePaymentRecovery({...recoveryScope, guestPayment: initiallyGuest});
    });
    const offersAccountChoice = customerAccountMode === "optional" &&
        !hasPaymentCustomer && allowsInlineRegistration;
    const guestRecoveryActive = !!recoveryContextState?.recovery?.guestAccessToken &&
        !TERMINAL_FAILURE_STATUSES.has(normaliseStatus(recoveryContextState.recovery.status));
    const guestPayment = guestRecoveryActive || customerAccountMode === "none" || (
        customerAccountMode === "optional" && !hasPaymentCustomer &&
        accountPathChoice !== PAYMENT_ACCOUNT_PATH_REGISTER
    );
    const activeConsentText = guestPayment
        ? (guestConsentText || (customerAccountMode === "none" ? consentText : ""))
        : consentText;
    const activeConsentVersion = guestPayment
        ? (guestConsentVersion || (customerAccountMode === "none" ? consentVersion : ""))
        : consentVersion;
    const canEnterPayment = guestPayment || customerIsAuthenticated || allowsInlineRegistration;
    const canUseSavedPaymentMethod = customerIsAuthenticated && !guestPayment &&
        (field.mode === "security-hold" || field.mode === "take-payment");
    const availableSavedPaymentMethods = canUseSavedPaymentMethod
        ? savedPaymentMethods.filter(method => method?.active !== false)
        : [];
    const [amount, setAmount] = useState("");
    const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState(() => (
        availableSavedPaymentMethods[0]?.id == null ? "" : String(availableSavedPaymentMethods[0].id)
    ));
    const [cardComplete, setCardComplete] = useState(false);
    const [cardError, setCardError] = useState("");
    const [consentAccepted, setConsentAccepted] = useState(false);
    const [error, setError] = useState("");
    const [paymentProcessing, setPaymentProcessing] = useState(false);
    const paymentFieldId = field.id || field.name || "payment";
    const amountControlId = formId
        ? `${formId}--${paymentFieldId}_amount`
        : `${paymentFieldId}_amount`;
    const cardControlId = formId
        ? `${formId}--${paymentFieldId}_card`
        : `${paymentFieldId}_card`;
    const inMemoryRecoveryRef = useRef(null);
    const checkedRecoveryRef = useRef(new Set());
    const authenticatedForPaymentRef = useRef(!!customerIsAuthenticated);
    const recoveryIdentity = getCustomerRecoveryIdentity(
        formContext.values,
        websiteContext.customerSession,
        guestPayment
    );
    const storedRecoveryPresentation = getPaymentRecoveryPresentation(
        recoveryContextState?.recovery,
        field,
        activeConsentText,
        activeConsentVersion,
        websiteContext.country
    );
    const recoveryPresentation = recoveryContextState?.ownershipVerified === false
        ? null
        : storedRecoveryPresentation;
    const recoveryStatus = recoveryPresentation?.status;
    const recoveryLocksAttempt = recoveryPresentation?.compatible === true &&
        !TERMINAL_FAILURE_STATUSES.has(normaliseStatus(recoveryStatus));
    const usesSavedPaymentMethod = canUseSavedPaymentMethod && selectedPaymentMethodId !== "";
    const selectedSavedPaymentMethod = availableSavedPaymentMethods.find(
        method => String(method.id) === String(selectedPaymentMethodId)
    );
    const savedPaymentMethodOptions = [
        ...availableSavedPaymentMethods.map(method => ({
            label: formatSavedPaymentMethod(method),
            value: String(method.id)
        })),
        {label: "Use another card", value: ""}
    ];
    const amountHelp = getCustomerAmountHelp(field, websiteContext.country);
    const themedChoiceStyle = {
        color: "var(--base-colour)",
        "--tw-ring-color": "var(--base-colour)"
    };

    const changeAccountPath = (event, nextPath) => {
        if (recoveryLocksAttempt) return;
        setAccountPathChoice(nextPath);
        if (field.onFieldChange) {
            field.onFieldChange(event, PAYMENT_ACCOUNT_PATH_FIELD_NAME, nextPath);
        } else if (formContext.setContext) {
            formContext.setContext(previous => ({
                ...previous,
                values: {...previous.values, [PAYMENT_ACCOUNT_PATH_FIELD_NAME]: nextPath}
            }));
        }
    };

    // A checked box must never carry forward to replacement wording. This also protects live builder/form updates
    // where React can keep the mounted payment control while the server-owned disclosure changes.
    useEffect(() => {
        setConsentAccepted(false);
    }, [activeConsentText, activeConsentVersion, field.id, formId]);

    useEffect(() => {
        if (customerIsAuthenticated) authenticatedForPaymentRef.current = true;
    }, [customerIsAuthenticated]);

    useEffect(() => {
        const existingRecovery = getExistingWebsitePaymentRecovery({
            channel,
            formId,
            fieldId: field.id,
            formValues: formContext.values,
            customerSession: websiteContext.customerSession,
            guestPayment
        });
        if (!existingRecovery) return;

        const presentation = getPaymentRecoveryPresentation(
            existingRecovery.recovery,
            field,
            activeConsentText,
            activeConsentVersion,
            websiteContext.country
        );
        inMemoryRecoveryRef.current = existingRecovery;
        setRecoveryContextState(existingRecovery);
        if (!presentation?.compatible || existingRecovery.ownershipVerified === false) return;
        if (field.customerEntersAmount && presentation.amountInputValue) {
            setAmount(presentation.amountInputValue);
        }
        if (presentation.paymentMethodId != null) {
            setSelectedPaymentMethodId(String(presentation.paymentMethodId));
        } else if (presentation.paymentMethodId === null) {
            setSelectedPaymentMethodId("");
        }
        if (presentation.restoreConsent) setConsentAccepted(true);
    }, [activeConsentText, activeConsentVersion, channel, field.currency, field.customerEntersAmount, field.fixedAmount,
        field.holdDurationHours, field.id, field.maximumAmount, field.minimumAmount, field.mode, formId,
        guestPayment, recoveryIdentity, websiteContext.country, websiteContext.customerSession]);

    // Once the authenticated customer returns to the form, safely ask the server whether the exact saved operation
    // has completed. The endpoint verifies customer, form, channel and provider ownership before returning details.
    useEffect(() => {
        const recovery = recoveryContextState?.recovery;
        if ((!customerIsAuthenticated && !guestPayment) || !recovery?.operationId ||
            recoveryContextState?.needsAutomaticReconciliation !== true) return;
        const checkKey = `${recoveryContextState.storageKey}:${recovery.operationId}`;
        if (checkedRecoveryRef.current.has(checkKey)) return;
        checkedRecoveryRef.current.add(checkKey);
        let cancelled = false;

        axios.post(
            `/api/v2/channels/${channel}/forms/${formId}/payment-operation/complete`,
            {
                operationId: recovery.operationId,
                guestAccessToken: guestPayment ? recovery.guestAccessToken : undefined
            }
        ).then(({data}) => {
            if (cancelled) return;
            const updated = {
                ...updateWebsitePaymentRecovery(recoveryContextState, data),
                ownershipVerified: true,
                needsAutomaticReconciliation: false
            };
            inMemoryRecoveryRef.current = updated;
            setRecoveryContextState(updated);
            const presentation = getPaymentRecoveryPresentation(
                updated.recovery,
                field,
                activeConsentText,
                activeConsentVersion,
                websiteContext.country
            );
            if (!presentation?.compatible) return;
            if (field.customerEntersAmount && presentation.amountInputValue) setAmount(presentation.amountInputValue);
            setSelectedPaymentMethodId(presentation.paymentMethodId == null ? "" : String(presentation.paymentMethodId));
            if (presentation.restoreConsent) setConsentAccepted(true);
        }).catch(recoveryError => {
            if (cancelled) return;
            if (recoveryError?.response?.status === 400) {
                const verified = {
                    ...recoveryContextState,
                    ownershipVerified: true,
                    needsAutomaticReconciliation: false
                };
                inMemoryRecoveryRef.current = verified;
                setRecoveryContextState(verified);
                const presentation = getPaymentRecoveryPresentation(
                    verified.recovery,
                    field,
                    activeConsentText,
                    activeConsentVersion,
                    websiteContext.country
                );
                if (!presentation?.compatible) return;
                if (field.customerEntersAmount && presentation.amountInputValue) setAmount(presentation.amountInputValue);
                setSelectedPaymentMethodId(presentation.paymentMethodId == null ? "" : String(presentation.paymentMethodId));
                if (presentation.restoreConsent) setConsentAccepted(true);
                return;
            }
            // Do not show locally saved payment details when the authenticated session cannot prove ownership.
            inMemoryRecoveryRef.current = null;
            setRecoveryContextState(null);
            setError(!guestPayment && (recoveryError?.response?.status === 401 || recoveryError?.response?.status === 403)
                ? "Please sign in to continue your card attempt."
                : "We couldn't check your previous card attempt. Please try again.");
        });

        return () => { cancelled = true; };
    }, [activeConsentText, activeConsentVersion, channel, customerIsAuthenticated, field, formId, guestPayment,
        recoveryContextState?.needsAutomaticReconciliation, recoveryContextState?.recovery?.operationId,
        recoveryContextState?.storageKey, websiteContext.country]);

    useEffect(() => {
        const registration = formContext.registerProcessor(async (_onProgress, _processorCount, formValues) => {
            setError("");
            setCardError("");
            if (!guestPayment && !authenticatedForPaymentRef.current && !allowsInlineRegistration) {
                const message = "Please sign in before submitting payment details.";
                setError(message);
                throw new Error(message);
            }
            if (!stripe || !elements) {
                const message = "Card entry is still loading. Please try again.";
                setError(message);
                throw new Error(message);
            }
            if (activeConsentText && !consentAccepted) {
                const message = "Please accept the payment consent before continuing.";
                setError(message);
                throw new Error(message);
            }

            const amountInCents = field.customerEntersAmount ? dollarsToCents(amount) : null;
            if (field.customerEntersAmount && (!amountInCents || amountInCents <= 0)) {
                const message = "Please enter a valid amount.";
                setError(message);
                throw new Error(message);
            }

            const requestSignature = JSON.stringify({
                amount: amountInCents,
                consentAccepted,
                paymentMethodId: usesSavedPaymentMethod ? Number(selectedPaymentMethodId) : null,
                mode: field.mode,
                currency: field.currency,
                fixedAmount: field.fixedAmount,
                holdDurationHours: field.holdDurationHours,
                consentVersion: activeConsentVersion,
                consentText: activeConsentText,
                guestPayment
            });
            const recoveryScope = {
                channel,
                formId,
                fieldId: field.id,
                formValues,
                customerSession: websiteContext.customerSession,
                guestPayment
            };
            const mountedRecovery = inMemoryRecoveryRef.current;
            if (mountedRecovery?.ownershipVerified === false) {
                const message = "We're checking your unfinished card attempt. Please wait a moment and submit again.";
                setError(message);
                throw new Error(message);
            }
            const expectedStorageKey = getPaymentRecoveryKey(recoveryScope);
            let recoveryContext = mountedRecovery &&
                !TERMINAL_FAILURE_STATUSES.has(normaliseStatus(mountedRecovery.recovery.status)) &&
                (mountedRecovery.storageKey === expectedStorageKey || mountedRecovery.ownershipVerified === true)
                ? mountedRecovery
                : getOrCreateWebsitePaymentRecovery(recoveryScope, requestSignature);

            // Keep a mounted fallback even when sessionStorage exists but writes are blocked (for example, strict
            // privacy settings or exhausted quota). A different customer-derived scope never reuses this fallback.
            if (mountedRecovery?.storageKey === recoveryContext.storageKey &&
                !TERMINAL_FAILURE_STATUSES.has(normaliseStatus(mountedRecovery.recovery.status)) &&
                (!recoveryContext.recovery.operationId || mountedRecovery.recovery.operationId)) {
                recoveryContext = mountedRecovery;
            } else {
                inMemoryRecoveryRef.current = recoveryContext;
            }

            // The start response may have been lost after the server created an operation. Never replay the same key
            // with changed amount or consent, even when this browser did not receive and store the operation id.
            if (hasWebsitePaymentRecoveryRequestChanged(recoveryContext.recovery, requestSignature)) {
                const message = "Your payment details have changed since this attempt began. Restore the previous amount to finish it safely.";
                setError(message);
                throw new Error(message);
            }
            setPaymentProcessing(true);
            setRecoveryContextState(recoveryContext);
            const hasStoredProviderAction = !!recoveryContext.recovery.operationId &&
                REPLAYABLE_OPERATION_STATUSES.has(normaliseStatus(recoveryContext.recovery.status));
            const continuesExistingGuestPayment = guestPayment &&
                (!recoveryContext.wasCreated || !!recoveryContext.recovery.operationId) &&
                !!recoveryContext.recovery.idempotencyKey && !!recoveryContext.recovery.guestAccessToken &&
                recoveryContext.recovery.requestSignature === requestSignature;

            try {
                if (guestPayment && !recoveryContext.recovery.guestAccessToken) {
                    throw new Error("This browser cannot securely continue a guest payment.");
                }

                // Do not create a customer account until the new card input has passed its client-side completeness
                // check. An existing provider action is different: it must resume without asking for card details.
                if (!guestPayment && !authenticatedForPaymentRef.current &&
                    !recoveryContext.recovery.operationId && !cardComplete &&
                    !usesSavedPaymentMethod && !hasStoredProviderAction) {
                    if (recoveryContext.wasCreated) {
                        discardUnstartedWebsitePaymentRecovery(recoveryContext);
                        inMemoryRecoveryRef.current = null;
                        setRecoveryContextState(null);
                    }
                    const message = "Please enter your card details.";
                    setCardError(message);
                    throw new Error(message);
                }

                if (!guestPayment && !authenticatedForPaymentRef.current) {
                    if (!formValues?.customer) {
                        if (recoveryContext.wasCreated) {
                            discardUnstartedWebsitePaymentRecovery(recoveryContext);
                            inMemoryRecoveryRef.current = null;
                            setRecoveryContextState(null);
                        }
                        throw new Error("Please sign in before submitting payment details.");
                    }
                    try {
                        await axios.post(
                            `/api/v2/channels/${channel}/forms/${formId}/payment-customer`,
                            {
                                fieldId: field.id,
                                // The password is removed from form state immediately after registration. Keep the
                                // request payload independent so that cleanup cannot race Axios request transforms.
                                customer: {...formValues.customer}
                            }
                        );
                        authenticatedForPaymentRef.current = true;
                        setPaymentCustomerEstablished(true);
                    } catch (registrationError) {
                        if (registrationError?.response?.status === 409) {
                            discardUnstartedWebsitePaymentRecovery(recoveryContext);
                            inMemoryRecoveryRef.current = null;
                            setRecoveryContextState(null);
                            throw new Error("Please sign in and try again, or check the details you entered.");
                        }
                        throw registrationError;
                    }
                }

                // Registration has already established the authenticated session. Do not send the credential to
                // payment endpoints or retain it in the final form response.
                if (formValues?.customer) delete formValues.customer.password;

                // A reload may happen after Stripe accepted the card but before the form response was saved. Ask the
                // server to recover the known operation before requiring the customer to enter card details again.
                let recoveredProviderAction = null;
                if (recoveryContext.recovery.operationId) {
                    try {
                        const recoveredResponse = await axios.post(
                            `/api/v2/channels/${channel}/forms/${formId}/payment-operation/complete`,
                            {
                                operationId: recoveryContext.recovery.operationId,
                                guestAccessToken: guestPayment
                                    ? recoveryContext.recovery.guestAccessToken
                                    : undefined
                            }
                        );
                        recoveryContext = updateWebsitePaymentRecovery(recoveryContext, recoveredResponse.data);
                        inMemoryRecoveryRef.current = recoveryContext;
                        setRecoveryContextState(recoveryContext);
                        if (normaliseStatus(recoveredResponse.data?.status) === "succeeded") {
                            if (guestPayment) rememberGuestAccessToken(
                                channel,
                                formId,
                                recoveredResponse.data.id,
                                recoveryContext.recovery.guestAccessToken
                            );
                            return formPaymentResult(field, recoveredResponse.data);
                        }
                        if (RESUMABLE_ACTION_STATUSES.has(normaliseStatus(recoveredResponse.data?.status)) &&
                            recoveredResponse.data?.clientSecret) {
                            recoveredProviderAction = recoveredResponse.data;
                        }
                    } catch (recoveryError) {
                        // A 400 means the provider has not confirmed the existing operation yet. Other failures may
                        // indicate authentication, ownership, throttling, or connectivity problems and must not start
                        // a second payment attempt.
                        if (recoveryError?.response?.status !== 400) throw recoveryError;
                    }
                }

                const resumeProviderAction = recoveredProviderAction != null || hasStoredProviderAction;
                // If Complete cannot yet settle a stored operation, replay Start with the exact idempotency key and
                // (for guests) continuation token. The server only returns that same operation; it rejects attempts
                // to use this path to begin a new guest payment from an authenticated session.
                if (!cardComplete && !usesSavedPaymentMethod && !resumeProviderAction) {
                    if (recoveryContext.wasCreated) {
                        discardUnstartedWebsitePaymentRecovery(recoveryContext);
                        inMemoryRecoveryRef.current = null;
                        setRecoveryContextState(null);
                    }
                    const message = "Please enter your card details.";
                    setCardError(message);
                    throw new Error(message);
                }

                let operation = recoveredProviderAction;
                if (!operation) {
                    let startResponse;
                    try {
                        startResponse = await axios.post(
                            `/api/v2/channels/${channel}/forms/${formId}/payment-operation/start`,
                            {
                                fieldId: field.id,
                                amount: amountInCents,
                                paymentMethodId: usesSavedPaymentMethod ? Number(selectedPaymentMethodId) : null,
                                idempotencyKey: recoveryContext.recovery.idempotencyKey,
                                useGuestPayment: guestPayment,
                                continueGuestPayment: continuesExistingGuestPayment,
                                guestAccessToken: guestPayment ? recoveryContext.recovery.guestAccessToken : undefined,
                                consentAccepted,
                                consentVersion: activeConsentVersion,
                                response: buildPaymentOperationResponseSnapshot(formValues, formId, guestPayment)
                            }
                        );
                    } catch (startError) {
                        // This distinct response means the browser saved its replay identity before the original
                        // request reached the server. It is the only safe reason to retire that guest record; all
                        // ownership mismatches and transient errors keep it available for investigation/retry.
                        if (continuesExistingGuestPayment && !recoveryContext.recovery.operationId &&
                            startError?.response?.status === 409) {
                            discardUnstartedWebsitePaymentRecovery(recoveryContext);
                            inMemoryRecoveryRef.current = null;
                            setRecoveryContextState(null);
                            throw new Error(
                                "The unfinished guest payment was not started. Submit again to pay using your account."
                            );
                        }
                        throw startError;
                    }
                    operation = startResponse.data;
                    recoveryContext = updateWebsitePaymentRecovery(recoveryContext, operation);
                    inMemoryRecoveryRef.current = recoveryContext;
                    setRecoveryContextState(recoveryContext);
                }

                // A provider confirmation may have succeeded before the form response was saved. A retry with the
                // same idempotency key must finish the response without asking Stripe to confirm the intent again.
                if (normaliseStatus(operation.status) === "succeeded") {
                    if (guestPayment) rememberGuestAccessToken(
                        channel,
                        formId,
                        operation.id,
                        recoveryContext.recovery.guestAccessToken
                    );
                    return formPaymentResult(field, operation);
                }
                if (TERMINAL_FAILURE_STATUSES.has(normaliseStatus(operation.status))) {
                    throw new Error("The previous card attempt was not successful. Please submit again to start a new attempt.");
                }
                if (!operation.clientSecret) {
                    throw new Error("The payment operation could not be resumed. Please try again.");
                }

                const card = usesSavedPaymentMethod || resumeProviderAction
                    ? null
                    : elements.getElement(CardElement);
                const confirmationOptions = getPaymentConfirmationOptions(
                    card,
                    usesSavedPaymentMethod,
                    resumeProviderAction
                );
                const result = operation.mode === "card-capture"
                    ? await stripe.confirmCardSetup(operation.clientSecret, confirmationOptions)
                    : await stripe.confirmCardPayment(operation.clientSecret, confirmationOptions);

                if (result.error) {
                    const message = getCustomerPaymentErrorMessage(result.error);
                    setError(message);
                    throw new Error(message);
                }

                const completeResponse = await axios.post(
                    `/api/v2/channels/${channel}/forms/${formId}/payment-operation/complete`,
                    {
                        operationId: operation.id,
                        guestAccessToken: guestPayment ? recoveryContext.recovery.guestAccessToken : undefined
                    }
                );

                const completed = completeResponse.data;
                recoveryContext = updateWebsitePaymentRecovery(recoveryContext, completed);
                inMemoryRecoveryRef.current = recoveryContext;
                setRecoveryContextState(recoveryContext);
                if (normaliseStatus(completed.status) !== "succeeded") {
                    throw new Error("The card confirmation is still being processed. Please try again shortly.");
                }
                if (guestPayment) rememberGuestAccessToken(
                    channel,
                    formId,
                    completed.id,
                    recoveryContext.recovery.guestAccessToken
                );
                return formPaymentResult(field, completed);
            } catch (err) {
                const message = !guestPayment &&
                    (err?.response?.status === 401 || err?.response?.status === 403)
                    ? "Please log in before submitting payment details."
                    : err?.response
                        ? "The payment could not be completed. Please try again."
                        : err?.message || "The payment could not be completed. Please try again.";
                if (message === "Please enter your card details.") setCardError(message);
                else setError(message);
                throw new Error(message);
            } finally {
                setPaymentProcessing(false);
            }
        });

        return () => registration?.destroy?.();
    // Form processors are one submission at a time. Context progress changes after each run, which re-registers this
    // field for a retry while the cleanup token removes only this specific registration.
    }, [activeConsentText, activeConsentVersion, allowsInlineRegistration, amount, cardComplete, channel,
        consentAccepted, customerIsAuthenticated, elements,
        field, formContext, formId, registerProcessor, selectedPaymentMethodId, stripe, usesSavedPaymentMethod,
        guestPayment, guestRecoveryActive, websiteContext.customerSession]);

    return (
        <div className="flex flex-col gap-3" role="group" aria-label={field.label || "Payment"}>
            {offersAccountChoice && !recoveryLocksAttempt ? (
                <p className="mb-0 text-sm leading-5 opacity-75">
                    Already have an account? <a className="font-medium" href={getPaymentSignInHref()}>Sign in</a>.
                </p>
            ) : null}
            {customerAccountMode === "optional" && !hasPaymentCustomer && !allowsInlineRegistration &&
            !recoveryLocksAttempt ? (
                <p className="mb-0 text-sm">
                    Pay as a guest, or <a className="font-medium" href={getPaymentSignInHref()}>sign in</a> to use your account.
                </p>
            ) : null}
            {customerAccountMode === "required" && !customerIsAuthenticated ? (
                <p className="mb-0 text-sm">
                    {allowsInlineRegistration
                        ? "Already have an account? "
                        : "Sign in to use a card for this form. "}
                    <a className="font-medium" href={getPaymentSignInHref()}>Sign in</a>
                    {allowsInlineRegistration ? " before continuing." : null}
                </p>
            ) : null}
            {recoveryContextState && recoveryPresentation?.compatible === false ? (
                <div role="alert" className="rounded rounded-theme border border-amber-300 bg-amber-50 p-3 text-sm text-gray-900">
                    This form has changed since your card attempt began, so it cannot be continued here. Please contact the business before trying again.
                </div>
            ) : null}
            {recoveryPresentation?.compatible && !error && !paymentProcessing ? (
                <p role="status" className="mb-0 text-sm leading-5 opacity-75">
                    {recoveryPresentation.message}
                </p>
            ) : null}
            {canEnterPayment &&
            (availableSavedPaymentMethods.length > 0 || (recoveryLocksAttempt && usesSavedPaymentMethod)) ? (
                <div className="flex flex-col gap-2">
                    <div className="text-sm font-medium">Choose a card</div>
                    <CustomRadioGroup
                        id={`${paymentFieldId}_payment_method`}
                        name={`${paymentFieldId}_payment_method`}
                        label="Choose a card"
                        style="list"
                        value={selectedPaymentMethodId}
                        disabled={recoveryLocksAttempt}
                        options={savedPaymentMethodOptions}
                        onChange={(_event, _name, value) => setSelectedPaymentMethodId(value)}
                    />
                    {recoveryLocksAttempt && usesSavedPaymentMethod && !selectedSavedPaymentMethod ? (
                        <p className="mb-0 text-sm">The saved card selected for this unfinished attempt is locked.</p>
                    ) : null}
                </div>
            ) : null}
            {canEnterPayment && field.customerEntersAmount ? (
                <div className="flex flex-col space-y-1">
                    <label htmlFor={amountControlId}>
                        Amount ({field.currency || "AUD"})*
                    </label>
                    <input
                        id={amountControlId}
                        type="number"
                        min={field.minimumAmount ? field.minimumAmount / 100 : undefined}
                        max={field.maximumAmount ? field.maximumAmount / 100 : undefined}
                        step="0.01"
                        required
                        readOnly={recoveryLocksAttempt}
                        aria-describedby={amountHelp ? `${amountControlId}_help` : undefined}
                        value={amount}
                        onChange={event => setAmount(event.target.value)}
                    />
                    {amountHelp ? (
                        <p id={`${amountControlId}_help`} className="mb-0 text-xs text-gray-500">{amountHelp}</p>
                    ) : null}
                    {recoveryLocksAttempt ? (
                        <p className="mb-0 text-xs text-gray-500">This amount is kept with your unfinished card attempt.</p>
                    ) : null}
                </div>
            ) : null}
            {canEnterPayment &&
            !usesSavedPaymentMethod &&
            !["succeeded", "requiresaction"].includes(recoveryStatus) ? (
                <WebsiteCardElement
                    id={cardControlId}
                    name={cardControlId}
                    required
                    error={cardError}
                    options={WEBSITE_PAYMENT_CARD_OPTIONS}
                    onChange={event => {
                        setCardComplete(event.complete);
                        if (event.error) setCardError(getCustomerPaymentErrorMessage(event.error));
                        else if (cardError) setCardError("");
                    }}
                />
            ) : null}
            {error ? <p role="alert" className="text-sm text-red-500">{error}</p> : null}
            {offersAccountChoice && !recoveryLocksAttempt ? (
                <Checkbox
                    id={`${paymentFieldId}_create_account`}
                    type="checkbox"
                    name={`${paymentFieldId}_create_account`}
                    label="Create an account and save this card"
                    description="We'll ask you to create a password so you can manage and use this card again."
                    value={accountPathChoice === PAYMENT_ACCOUNT_PATH_REGISTER}
                    style={themedChoiceStyle}
                    onChange={(event, _name, checked) => changeAccountPath(
                        event,
                        checked ? PAYMENT_ACCOUNT_PATH_REGISTER : PAYMENT_ACCOUNT_PATH_GUEST
                    )}
                />
            ) : null}
            {canEnterPayment && activeConsentText ? (
                <Checkbox
                    id={`${paymentFieldId}_consent`}
                    type="checkbox"
                    name={`${paymentFieldId}_consent`}
                    label={activeConsentText}
                    required
                    disabled={recoveryLocksAttempt && !!recoveryContextState?.recovery?.operationId}
                    value={consentAccepted}
                    style={themedChoiceStyle}
                    onChange={(_event, _name, checked) => setConsentAccepted(checked)}
                />
            ) : null}
        </div>
    );
}

PaymentElementInner.propTypes = {
    field: PropTypes.object.isRequired,
    consentText: PropTypes.string.isRequired,
    consentVersion: PropTypes.string.isRequired,
    guestConsentText: PropTypes.string,
    guestConsentVersion: PropTypes.string,
    savedPaymentMethods: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number.isRequired,
        brand: PropTypes.string,
        lastFour: PropTypes.string,
        expiryMonth: PropTypes.number,
        expiryYear: PropTypes.number,
        active: PropTypes.bool
    }))
};

export default function WebsitePaymentField(field) {
    const formContext = useContext(FormContext);
    const websiteContext = formContext.websiteContext || {};
    const [stripeConfig, setStripeConfig] = useState(null);
    const [stripeClient, setStripeClient] = useState(null);
    const [loadError, setLoadError] = useState("");
    const [loadAttempt, setLoadAttempt] = useState(0);
    const [submissionBlockError, setSubmissionBlockError] = useState("");

    useEffect(() => {
        if (websiteContext.builder || websiteContext.picker) return;
        let cancelled = false;
        setStripeConfig(null);
        setStripeClient(null);
        setLoadError("");
        setSubmissionBlockError("");
        axios
            .get(`/api/v2/channels/${websiteContext.channel}/forms/${formContext.id}/payment-config`)
            .then(({data}) => {
                if (cancelled) return;
                const configurationError = getPaymentConfigurationError(data, field);
                if (configurationError) {
                    setLoadError(configurationError);
                    return;
                }
                setStripeConfig(data);
            })
            .catch(() => {
                if (!cancelled) setLoadError(PAYMENT_UNAVAILABLE_MESSAGE);
            });
        return () => { cancelled = true; };
    }, [field.allowGuestPayment, field.customerAccountMode, field.mode, formContext.id, loadAttempt,
        websiteContext.builder, websiteContext.channel, websiteContext.picker]);

    useEffect(() => {
        if (!stripeConfig?.publishableKey || !stripeConfig?.stripeAccountId) return;
        let cancelled = false;
        Promise.resolve()
            .then(() => loadStripe(stripeConfig.publishableKey, {stripeAccount: stripeConfig.stripeAccountId}))
            .then(client => {
                if (cancelled) return;
                if (!client) throw new Error("Stripe did not initialise");
                setStripeClient(client);
            })
            .catch(() => {
                if (!cancelled) setLoadError(PAYMENT_UNAVAILABLE_MESSAGE);
            });
        return () => { cancelled = true; };
    }, [stripeConfig]);

    const submissionBlockMessage = loadError || (!stripeClient ? PAYMENT_LOADING_MESSAGE : null);
    useEffect(() => {
        if (websiteContext.builder || websiteContext.picker || !submissionBlockMessage) return;

        const registration = formContext.registerProcessor(() => {
            setSubmissionBlockError(submissionBlockMessage);
            return Promise.reject(new Error(submissionBlockMessage));
        });
        return () => registration?.destroy?.();
    }, [formContext.registerProcessor, submissionBlockMessage, websiteContext.builder, websiteContext.picker]);

    let paymentControl;
    if (websiteContext.builder || websiteContext.picker) {
        paymentControl = <PaymentPreview {...field}/>;
    } else if (loadError) {
        paymentControl = (
            <div className="flex flex-col items-start gap-2">
                <p role="alert" className="mb-0 text-sm text-red-500">{loadError}</p>
                <button
                    type="button"
                    className="text-link text-sm"
                    onClick={() => setLoadAttempt(attempt => attempt + 1)}
                >
                    Try secure card entry again
                </button>
            </div>
        );
    } else if (!stripeClient) {
        paymentControl = (
            <>
                <p role="status" className="text-sm opacity-80">Loading secure card entry…</p>
                {submissionBlockError ? (
                    <p role="alert" className="text-sm text-red-500">{submissionBlockError}</p>
                ) : null}
            </>
        );
    } else {
        paymentControl = (
            <Elements stripe={stripeClient}>
                <PaymentElementInner
                    field={field}
                    consentText={stripeConfig.consentText}
                    consentVersion={stripeConfig.consentVersion}
                    guestConsentText={stripeConfig.guestConsentText}
                    guestConsentVersion={stripeConfig.guestConsentVersion}
                    savedPaymentMethods={stripeConfig.savedPaymentMethods || []}
                />
            </Elements>
        );
    }

    return (
        <PaymentFieldFrame field={field} country={websiteContext.country}>
            {paymentControl}
        </PaymentFieldFrame>
    );
}
